import { CompanyPeriodRequest, DataCollectionType } from '../../schemas/CompanyPeriodRequest.schema';
import { GetDataCollectionRequestsV2Response } from '../../schemas/CompanyPeriodRequestV2.schema';
import {
  BulkUpsertKpiCompanySettingsResponse,
  KpiCollectionCompanySettings,
  KpiCollectionCompanySettingsPayload,
} from '../../schemas/KpiCollectionCompanySettings.schema';
import {
  KpiCollectionConfiguration,
  KpiCollectionConfigurationResponse,
} from '../../schemas/KpiCollectionConfiguration.schema';
import { EmailAuditRow } from '../../schemas/EmailAudit.schema';
import { CompanyRespondentStatuses } from '../../schemas/RespondentApprovalStatus.schema';
import { MaggieHttp } from '../MaggieHttp';

export async function fetchKpiCollectionConfigurations() {
  const { data } = await MaggieHttp.maggie.get<KpiCollectionConfigurationResponse[]>('/kpiCollectionSetting');
  return data;
}

export async function createKpiCollectionConfiguration(configuration: KpiCollectionConfiguration) {
  const { data } = await MaggieHttp.maggie.post<
    KpiCollectionConfiguration,
    KpiCollectionConfigurationResponse
  >('/kpiCollectionSetting', configuration);
  return data;
}

export async function updateKpiCollectionConfiguration(
  id: KpiCollectionConfigurationResponse['id'],
  configuration: KpiCollectionConfiguration
) {
  const { data } = await MaggieHttp.maggie.put<
    KpiCollectionConfiguration,
    KpiCollectionConfigurationResponse
  >(`/kpiCollectionSetting/${id}`, configuration);
  return data;
}

export async function deleteKpiCollectionConfiguration(id: KpiCollectionConfigurationResponse['id']) {
  const { data } = await MaggieHttp.maggie.delete<KpiCollectionConfigurationResponse>(
    `/kpiCollectionSetting/${id}`
  );
  return data;
}

export async function fetchKpiCollectionCompanySettings() {
  const { data } = await MaggieHttp.maggie.get<KpiCollectionCompanySettings[]>(
    '/kpiCollectionCompanySetting'
  );
  return data;
}

export async function bulkUpdateKpiCollectionCompanySettings(
  settings: KpiCollectionCompanySettingsPayload[]
) {
  const { data } = await MaggieHttp.maggie.post<
    { data: KpiCollectionCompanySettingsPayload[] },
    BulkUpsertKpiCompanySettingsResponse
  >('/kpiCollectionCompanySetting/bulk', { data: settings });
  return data;
}

interface CompanyPeriodRequestResponse {
  message: string;
  requests: CompanyPeriodRequest[];
  success: boolean;
  total: number;
}
export async function fetchCompanyPeriodRequests(dataType: DataCollectionType, limit: number = 1_000_000) {
  const { data } = await MaggieHttp.maggie.get<CompanyPeriodRequestResponse>(
    '/starbreaker-sync/data-collection-requests',
    {
      params: {
        dataType,
        limit,
      },
    }
  );
  return data;
}

export async function fetchCompanyPeriodRequestsV2() {
  const { data } = await MaggieHttp.maggie.get<GetDataCollectionRequestsV2Response>(
    '/starbreaker-sync/data-collection-requests-v2'
  );
  return data;
}

// No companyIds → Starbreaker returns the firm's full onboarded set in one (unpaginated) call.
export async function fetchRespondentApprovalStatuses(): Promise<CompanyRespondentStatuses[]> {
  const { data } = await MaggieHttp.maggie.get<CompanyRespondentStatuses[]>(
    '/starbreaker-sync/respondent-statuses'
  );
  return data;
}

export type SuggestOutcome =
  | 'created'
  | 'promoted'
  | 'already_pending'
  | 'already_approved'
  | 'blocked_rejected';

export type WithdrawOutcome = 'firm_removed' | 'withdrawn' | 'not_found' | 'noop_decided' | 'noop_not_owner';

interface RespondentSuggestionRequest {
  companyId: number;
  emails: string[];
}

export interface RespondentOutcomeItem<T> {
  email: string;
  outcome: T;
}

// The suggest/withdraw endpoints (proxied by Maggie) return per-email outcomes plus a numeric
// summary. Only `results` is consumed here, so `summary` stays loosely typed.
export interface SuggestRespondentsResponse {
  results: RespondentOutcomeItem<SuggestOutcome>[];
  summary: Record<string, number>;
}

export interface WithdrawRespondentsResponse {
  results: RespondentOutcomeItem<WithdrawOutcome>[];
  summary: Record<string, number>;
}

export async function suggestRespondents(
  companyId: number,
  emails: string[]
): Promise<SuggestRespondentsResponse> {
  const { data } = await MaggieHttp.maggie.post<RespondentSuggestionRequest, SuggestRespondentsResponse>(
    '/starbreaker-sync/respondent-suggestions',
    { companyId, emails }
  );
  return data;
}

export async function withdrawRespondents(
  companyId: number,
  emails: string[]
): Promise<WithdrawRespondentsResponse> {
  const { data } = await MaggieHttp.maggie.post<RespondentSuggestionRequest, WithdrawRespondentsResponse>(
    '/starbreaker-sync/respondent-suggestions/withdraw',
    { companyId, emails }
  );
  return data;
}

// Per-recipient email-delivery rows for a single V2 request (Manage Requests
// detail modal). The client-side re-filter is defensive: an older backend that
// doesn't recognize `requestId` strips it and returns the firm's full set, so
// we never render another request's rows into this modal.
export async function fetchRequestEmailAudits(params: {
  requestId: string;
  requestSource: 'firm' | 'universal';
}): Promise<EmailAuditRow[]> {
  const { data } = await MaggieHttp.maggie.get<EmailAuditRow[]>('/starbreaker-sync/email-audits', {
    params,
  });
  return data.filter(
    (row) => row.requestId === params.requestId && row.requestSource === params.requestSource
  );
}
